// angular
import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  Component,
  effect,
  inject,
  input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { TextSummarizerComponent } from '@app/core/components/text-summarizer/text-summarizer.component';
import { BarcodeDialogComponent } from '@app/core/dialogs/barcode-dialog/barcode-dialog.component';
import { ContactDialogComponent } from '@app/core/dialogs/contact-dialog/contact-dialog.component';
import { ButtonsMenuComponent } from '@app/core/components/buttons-menu/buttons-menu.component';
// services
import { DialogService } from '@app/core/shared/services/dialog.service';
import { UserService } from '@app/shared/services/user.service';
// types & enums & env
import { IPortfolioHeaderData } from '@app/+portfolio/shared/interfaces/portfolio-header-data.interface';
import { IHeaderMenu } from '@app/+portfolio/shared/interfaces/portfolio-header-menu.interface';
import { ContactDialogItem } from '@app/core/dialogs/contact-dialog/shared/contact-dialog-item.type';
import { MenuButtons } from '@app/layout/shared/types/menu-buttons.type';
import { ButtonsMenuTypes } from '@app/shared/types/languages.type';
import { IUser } from '@app/shared/types/user.type';
import { I18n } from '@app/shared/enums/i18n.enum';
import { environment } from '@environments/environment';
// pipes & data
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
import { portfolioHeaderMenuData } from '@app/+portfolio/shared/constants/portfolio-header-menu-data';
// constants
import { languagesData } from '@app/shared/constants/languages';

@Component({
  selector: 'pwj-portfolio-header',
  standalone: true,
  imports: [
    RouterLink,
    ButtonComponent,
    NgOptimizedImage,
    NgClass,
    TranslateModule,
    TextSummarizerComponent,
    TranslateDataPipe,
    ImageSourcePipe,
    ButtonsMenuComponent,
  ],
  templateUrl: './portfolio-header.component.html',
  styleUrl: './portfolio-header.component.scss',
})
export class PortfolioHeaderComponent implements OnInit, OnChanges {
  public languagesMenuItems!: MenuButtons[];
  public baseUrl: string = environment.baseUrl.base;
  public currentFragment: string | null = 'category';
  public headerMenu: IHeaderMenu[] = portfolioHeaderMenuData;
  public pageLink!: string;
  public langs: ButtonsMenuTypes[] = languagesData;
  public selectedLang: I18n = environment.defaultLang;
  public _userData!: IUser;

  private _contactData!: ContactDialogItem[];

  // inputs
  headerData = input.required<IPortfolioHeaderData>();

  // dependencies
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _translateService = inject(TranslateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _userService = inject(UserService);

  constructor() {
    // set current lang
    this.selectedLang = (I18n as any)[this._translateService.currentLang];
    this._translateService.onLangChange.subscribe(data => {
      this.selectedLang = (I18n as any)[data.lang];
    });
    afterNextRender(() => {
      // set page link
      this.pageLink = window.location.href;
    });

    // set languages menu data
    this.languagesMenuItems = languagesData.map(language => {
      return {
        text: language.name,
        id: language.code,
      };
    });

    effect(() => {
      this._userData = this._userService.userSignal();
    });
  }

  ngOnInit(): void {
    this._activatedRoute.fragment.subscribe(fragment => {
      this.currentFragment = fragment;
    });
  }

  ngOnChanges(): void {
    if (this.headerData) {
      // set contact data
      this._contactData = [
        {
          id: 1,
          description: 'contactUsDialog.voiceCallWithSupplier',
          details: this?.headerData()?.mobile,
          buttonIcon: 'mobile',
          buttonText: 'contactUsDialog.call',
          link: `tell:${this?.headerData()?.mobile}`,
        },
        {
          id: 2,
          description: 'contactUsDialog.chatOnWhatsApp',
          details: this?.headerData()?.mobile,
          buttonIcon: 'phone',
          buttonText: 'contactUsDialog.whatsApp',
          link: `https://wa.me/${this?.headerData()?.mobile}`,
        },
        {
          id: 3,
          description: 'contactUsDialog.viewTheCompanyWebsite',
          buttonIcon: 'globe',
          buttonText: 'contactUsDialog.viewSite',
          link: this.headerData()?.website_url,
        },
      ];
    }
  }

  public setLang(lang: string) {
    this.selectedLang = (I18n as any)[lang];
    this._translateService.use(lang.toLowerCase());

    // set profile lang
    if (this._userData.username) {
      this._userService.updateProfileLanguage(lang.toLowerCase());
    }
  }

  public openQrCodeDialog() {
    this._dialogService.open(BarcodeDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'products.qrCode',
        productTitle: this.headerData().title?.['en'],
        productCode: this.headerData().code,
        barcodeContent: this.pageLink,
      },
    });
  }

  public contactUs() {
    const dialogRef = this._dialogService.open(ContactDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'global.contact',
        contactsData: this._contactData,
      },
    });

    dialogRef.afterClosed().subscribe(_ => {});
  }
}
